/**
 * routes.js - Configuration des routes de l'application
 * 
 * Ce fichier centralise la définition de toutes les pages de l'application.
 * Il utilise le "lazy loading" pour optimiser le chargement de l'application.
 * 
 * Concept important: Lazy Loading
 * ================================
 * Le lazy loading est une technique qui permet de charger les pages uniquement
 * quand l'utilisateur y accède, et pas tout au démarrage de l'application.
 * 
 * Sans lazy loading:
 * - Au démarrage, l'application charge TOUT le code de toutes les pages
 * - Le temps de chargement initial est long
 * - L'utilisateur télécharge du code qu'il n'utilisera peut-être jamais
 * 
 * Avec lazy loading:
 * - Au démarrage, l'application charge seulement le code nécessaire
 * - Quand l'utilisateur navigue vers une page, le code de cette page est chargé
 * - Le temps de chargement initial est beaucoup plus court
 * 
 * La fonction lazy() de React crée une "promesse" qui sera résolue quand
 * le code de la page sera demandé.
 */

import { lazy } from "react";

// ============================================================================
// LAZY LOADING DES PAGES
// ============================================================================

/**
 * Page de connexion
 * Cette page permet à l'utilisateur de se connecter avec son nom et mot de passe
 */
const Login = lazy(() => import("../features/auth/components/Login"));

/**
 * Liste des programmes
 * Cette page affiche tous les programmes de l'événement, filtrés par jour
 */
const ProgrammeList = lazy(() => import("../features/programme/components/ProgrammeList"));

/**
 * Formulaire de création/édition de programme
 * Cette page permet de créer un nouveau programme ou d'en modifier un existant
 */
const ProgrammeForm = lazy(() => import("../features/programme/components/ProgrammeForm"));

/**
 * Gestion des participants d'une session
 * Cette page permet d'ajouter ou de retirer des participants d'une session de programme
 */
const ProgrammeParticipants = lazy(() => import("../features/programme/components/ProgrammeParticipants"));

/**
 * Liste des informations pratiques
 * Cette page affiche toutes les infos pratiques (hôtels, taxis, restaurants, etc.)
 */
const InfosList = lazy(() => import("../features/infos/components/InfosList"));

/**
 * Formulaire de création/édition d'info pratique
 * Cette page permet de créer une nouvelle info pratique ou d'en modifier une existante
 */
const InfoForm = lazy(() => import("../features/infos/components/InfoForm"));

/**
 * Liste de toutes les personnes
 * Cette page affiche toutes les personnes enregistrées dans le système
 */
const PeopleList = lazy(() => import("../features/participants/components/PeopleList"));

/**
 * Configuration des jours de l'événement
 * Cette page permet de définir les jours de l'événement (J1, J2, J3, etc.)
 */
const SettingsDays = lazy(() => import("../features/settings/components/SettingsDays"));

/**
 * Configuration de la bannière partenaires
 * Cette page permet de définir l'image de la bannière des partenaires
 */
const SettingsPartnersBanner = lazy(() => import("../features/settings/components/SettingsPartnersBanner"));

/**
 * Configuration des liens réseaux sociaux
 * Cette page permet de gérer les liens vers les réseaux sociaux (Facebook, Twitter, etc.)
 */
const SettingsSocialLinks = lazy(() => import("../features/settings/components/SettingsSocialLinks"));

/**
 * Configuration des lieux
 * Cette page permet de gérer les lieux de l'événement (salles, espaces, etc.)
 */
const SettingsLocations = lazy(() => import("../features/settings/components/SettingsLocations"));

/**
 * Configuration des catégories d'infos
 * Cette page permet de gérer les catégories d'informations pratiques
 */
const SettingsInfoCategories = lazy(() => import("../features/settings/components/SettingsInfoCategories"));

// ============================================================================
// EXPORT DES COMPOSANTS
// ============================================================================
// Ces exports permettent aux autres fichiers d'importer ces composants
// pour les utiliser dans la configuration des routes (voir App.js)

export {
  Login,
  ProgrammeList,
  ProgrammeForm,
  ProgrammeParticipants,
  InfosList,
  InfoForm,
  PeopleList,
  SettingsDays,
  SettingsPartnersBanner,
  SettingsSocialLinks,
  SettingsLocations,
  SettingsInfoCategories,
};
